import * as p from '@clack/prompts'
import { spinner } from '@core-engine/utils/spinner.js'
import pc from 'picocolors'

import { parseDotenvFile, pullVercelEnvValue } from './env-pull.js'
import { sanitizeVercelProjectName, versionedVercelProjectName } from './project.js'
import { runVercel } from './runner.js'
import type { BlobProvisionResult, RunVercelResult, VercelRunner } from './types.js'

/** Non-interactive provisioning can't wait on a human; keep it bounded. */
const PROVISION_TIMEOUT_MS = 180_000
/** Interactive provisioning waits on the user clicking through Vercel's prompts. */
const INTERACTIVE_PROVISION_TIMEOUT_MS = 600_000
/** Bounded name search: base plus -v2 … -v9, then one unique last-resort suffix. */
const MAX_STORE_NAME_ATTEMPTS = 10

/** Derive a Blob store name from the project name (Vercel project naming rules apply). */
export function blobStoreName(projectName: string): string {
  return sanitizeVercelProjectName(`${projectName}-blob`)
}

/**
 * A 409/conflict from `blob create-store` always means the name is taken;
 * Vercel's wording varies across CLI versions, so match the status code too.
 */
function isNameTaken(result: RunVercelResult): boolean {
  return /already exists|already taken|already in use|name.*taken|conflict|\b409\b/i.test(
    `${result.stdout}\n${result.stderr}`
  )
}

/**
 * Store-name candidate for the given attempt: the base name, then `-v2` …
 * `-v9`, then a unique base36-timestamp suffix — so name conflicts alone can
 * never exhaust the search.
 */
function candidateStoreName(baseName: string, attempt: number): string {
  if (attempt < MAX_STORE_NAME_ATTEMPTS) return versionedVercelProjectName(baseName, attempt)
  const suffix = `-${Date.now().toString(36)}`
  return `${baseName.slice(0, 100 - suffix.length)}${suffix}`
}

/**
 * `create-store --yes` creates the store first and only then connects it; the
 * connect step fails when the linked project already has a
 * BLOB_READ_WRITE_TOKEN env var — i.e. a Blob store is already connected, and
 * its token is reusable via env pull. No store name can succeed here.
 */
function isEnvVarConflict(result: RunVercelResult): boolean {
  return /already has an existing environment variable/i.test(`${result.stdout}\n${result.stderr}`)
}

/** Store id from the "Blob store created: <name> (store_…)" success line. */
function createdStoreId(result: RunVercelResult): string | undefined {
  return /Blob store created:[^(]*\((store_[A-Za-z0-9]+)\)/.exec(
    `${result.stdout}\n${result.stderr}`
  )?.[1]
}

/**
 * Best-effort removal of the store a failed-connect attempt just created, so
 * retried inits don't accumulate orphaned stores. `delete-store` runs a full
 * env pull over `.env.local` afterwards — callers are covered by the flow's
 * `guardEnvLocal`.
 */
async function deleteOrphanStore(
  runner: VercelRunner,
  cwd: string,
  createResult: RunVercelResult,
  env?: NodeJS.ProcessEnv
): Promise<void> {
  const storeId = createdStoreId(createResult)
  if (!storeId) return
  await runVercel(runner, ['blob', 'delete-store', storeId, '--yes'], {
    cwd,
    mode: 'capture',
    timeoutMs: PROVISION_TIMEOUT_MS,
    env
  })
}

/**
 * Run `vercel blob create-store` with captured output, retrying with a version
 * suffix (`-v2`, `-v3`, …) when the store name is already taken. `--access
 * public` matches how uploads are served (direct public URLs, like the R2
 * setup) and `--yes` connects the store to all environments of the linked
 * project — which is what injects BLOB_READ_WRITE_TOKEN for the env pull.
 */
async function createStoreQuiet(
  runner: VercelRunner,
  cwd: string,
  baseName: string,
  env?: NodeJS.ProcessEnv
): Promise<{ result: RunVercelResult; storeName: string }> {
  let result: RunVercelResult | undefined
  let storeName = baseName

  for (let attempt = 1; attempt <= MAX_STORE_NAME_ATTEMPTS; attempt++) {
    storeName = candidateStoreName(baseName, attempt)
    result = await runVercel(
      runner,
      ['blob', 'create-store', storeName, '--access', 'public', '--yes'],
      { cwd, mode: 'capture', timeoutMs: PROVISION_TIMEOUT_MS, env }
    )
    if (result.success || !isNameTaken(result)) break
  }

  return { result: result as RunVercelResult, storeName }
}

/**
 * Provision a Vercel Blob store when a human is present. Mirrors the Neon
 * flow's quiet-first shape: each candidate name first runs with captured
 * output, which keeps Vercel's banner noise off the terminal and succeeds on
 * any account that needs no extra confirmation. Taken names bump the version
 * suffix and stay quiet. A project that already has a connected store (its
 * BLOB_READ_WRITE_TOKEN env var blocks connecting another) reuses the
 * existing token after removing the orphan store the attempt created. An
 * unknown quiet failure reruns once attached to the
 * terminal — without `--yes`, so the user can answer Vercel's own prompts —
 * and when that rerun also fails, the quiet version-bump search resumes:
 * inherit mode captures no output, so a name conflict there is invisible
 * (e.g. the quiet attempt created the store but died before connecting it).
 * The BLOB_READ_WRITE_TOKEN is then pulled into a temp file so BetterStart's
 * `.env.local` is never overwritten. Callers must stop any active spinner
 * first — inherited stdio and a clack spinner fight over the TTY.
 */
export async function provisionBlobStoreInteractive(
  runner: VercelRunner,
  cwd: string,
  options: { name: string; env?: NodeJS.ProcessEnv }
): Promise<BlobProvisionResult> {
  const quietSpinner = spinner()
  quietSpinner.start('Creating your Vercel Blob store')

  let terminalFallbackRan = false
  let lastResult: RunVercelResult | undefined

  const pullToken = async (
    activeSpinner: ReturnType<typeof spinner>,
    storeName?: string
  ): Promise<BlobProvisionResult> => {
    const token = await pullBlobToken(runner, cwd, options.env)
    if (!token) {
      activeSpinner.stop('No BLOB_READ_WRITE_TOKEN came back from Vercel')
      return { ...(storeName && { storeName }), failure: 'env-pull-empty' }
    }
    activeSpinner.clear()
    return { token, ...(storeName && { storeName }) }
  }

  for (let attempt = 1; attempt <= MAX_STORE_NAME_ATTEMPTS; attempt++) {
    const storeName = candidateStoreName(options.name, attempt)
    const result = await runVercel(
      runner,
      ['blob', 'create-store', storeName, '--access', 'public', '--yes'],
      { cwd, mode: 'capture', timeoutMs: PROVISION_TIMEOUT_MS, env: options.env }
    )
    lastResult = result

    if (result.success) {
      quietSpinner.message('Retrieving the Blob read-write token')
      return pullToken(quietSpinner, storeName)
    }
    if (isEnvVarConflict(result)) {
      // A store is already connected to the project; remove the orphan this
      // attempt created and reuse the existing token.
      await deleteOrphanStore(runner, cwd, result, options.env)
      quietSpinner.message('Retrieving the Blob read-write token')
      return pullToken(quietSpinner)
    }
    if (isNameTaken(result)) continue // taken name → bump the version suffix

    // Unknown quiet failure. Rerun once attached to the terminal so the user
    // can answer any Vercel prompts the captured run could not.
    if (terminalFallbackRan) break
    terminalFallbackRan = true

    // The quiet attempt cannot answer Vercel's prompts; erase the spinner (it
    // would fight the inherited stdio) and let the user click through them.
    quietSpinner.clear()
    p.log.info(
      `Create your Blob store in the Vercel prompts below ${pc.dim(
        '(connect it to all environments).'
      )}`
    )

    const add = await runVercel(runner, ['blob', 'create-store', storeName, '--access', 'public'], {
      cwd,
      mode: 'inherit',
      timeoutMs: INTERACTIVE_PROVISION_TIMEOUT_MS,
      env: options.env
    })

    if (add.success) {
      const pullSpinner = spinner()
      pullSpinner.start('Retrieving the Blob read-write token')
      return pullToken(pullSpinner, storeName)
    }
    if (add.timedOut) return { failure: 'timeout' }
    lastResult = add

    quietSpinner.start('Creating your Vercel Blob store')
  }

  quietSpinner.stop('Vercel Blob provisioning did not complete')
  return { failure: lastResult?.timedOut ? 'timeout' : 'provision-failed' }
}

/**
 * Non-interactive provisioning for `--yes`/CI with a `VERCEL_TOKEN`. A single
 * captured attempt; failures return the combined output as `detail` for
 * debugging so a headless run never hangs on a prompt.
 */
export async function provisionBlobStore(
  runner: VercelRunner,
  cwd: string,
  options: { name: string; env?: NodeJS.ProcessEnv }
): Promise<BlobProvisionResult> {
  const provisionSpinner = spinner()
  provisionSpinner.start('Creating your Vercel Blob store')

  const { result, storeName } = await createStoreQuiet(runner, cwd, options.name, options.env)

  if (!result.success && isEnvVarConflict(result)) {
    // A store is already connected to the project; remove the orphan this
    // attempt created and reuse the existing token.
    await deleteOrphanStore(runner, cwd, result, options.env)
    provisionSpinner.message('Retrieving the Blob read-write token')
    const token = await pullBlobToken(runner, cwd, options.env)
    if (!token) {
      provisionSpinner.stop('No BLOB_READ_WRITE_TOKEN came back from Vercel')
      return { failure: 'env-pull-empty' }
    }
    provisionSpinner.clear()
    return { token }
  }

  if (!result.success) {
    provisionSpinner.stop('Vercel Blob provisioning did not complete')
    const combined = `${result.stdout}\n${result.stderr}`.trim()
    return {
      failure: result.timedOut ? 'timeout' : 'provision-failed',
      detail: combined || undefined
    }
  }

  provisionSpinner.message('Retrieving the Blob read-write token')
  const token = await pullBlobToken(runner, cwd, options.env)
  if (!token) {
    provisionSpinner.stop('No BLOB_READ_WRITE_TOKEN came back from Vercel')
    return { storeName, failure: 'env-pull-empty' }
  }

  provisionSpinner.clear()
  return { token, storeName }
}

/** Pull the development env into a temp dotenv, extract the Blob token, delete it. */
function pullBlobToken(
  runner: VercelRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<string | undefined> {
  return pullVercelEnvValue(runner, cwd, readBlobTokenFromDotenv, env)
}

/**
 * Extract the Blob read-write token from a pulled dotenv file. Connecting a
 * store writes `BLOB_READ_WRITE_TOKEN` by default, but a custom env-var prefix
 * produces `<PREFIX>_READ_WRITE_TOKEN`; fall back to any `vercel_blob_rw_…`
 * value so a renamed connection still resolves.
 */
export function readBlobTokenFromDotenv(filePath: string): string | undefined {
  const vars = parseDotenvFile(filePath)

  const isBlobToken = (v: string | undefined): v is string => !!v && v.startsWith('vercel_blob_rw_')

  if (isBlobToken(vars.get('BLOB_READ_WRITE_TOKEN'))) return vars.get('BLOB_READ_WRITE_TOKEN')
  for (const [key, value] of vars) {
    if (/_READ_WRITE_TOKEN$/.test(key) && isBlobToken(value)) return value
  }
  for (const value of vars.values()) {
    if (isBlobToken(value)) return value
  }
  return undefined
}
